import { useCallback, useEffect, useRef } from 'react'

/**
 * Hold to record.
 *
 * Two ways in, because the cost of getting this wrong is a note that never
 * gets taken:
 *
 *   hold and release   — the fast path, thumb never leaves the glass
 *   tap                — locks recording on, tap again to save
 *
 * A short press is treated as a lock rather than a cancel. A parent who taps
 * when they meant to hold should end up recording, not staring at a button
 * that did nothing.
 *
 * The pressure ring is driven from an animation frame writing a custom
 * property, so it tracks the voice without re-rendering anything.
 */

const LOCK_MS = 380

export function CaptureButton({
  recording,
  locked,
  getLevel,
  disabled,
  onStart,
  onCommit,
  onLock,
}: {
  recording: boolean
  locked: boolean
  getLevel: () => number
  disabled?: boolean
  onStart: () => void
  onCommit: () => void
  onLock: () => void
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const pressedAt = useRef(0)
  const rafRef = useRef<number | null>(null)
  const heldRef = useRef(false)

  // Live pressure ring. Stops the moment recording does — a ring that keeps
  // breathing after release reads as "still listening".
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!recording) {
      el.style.setProperty('--level', '0')
      return
    }
    const tick = () => {
      el.style.setProperty('--level', getLevel().toFixed(3))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [recording, getLevel])

  const buzz = (ms: number) => {
    try { navigator.vibrate?.(ms) } catch { /* not supported, not important */ }
  }

  const press = useCallback(() => {
    if (disabled) return
    // Already locked on: this press is the save.
    if (locked) {
      buzz(14)
      onCommit()
      return
    }
    pressedAt.current = Date.now()
    heldRef.current = true
    buzz(8)
    onStart()
  }, [disabled, locked, onCommit, onStart])

  const release = useCallback(() => {
    if (!heldRef.current) return
    heldRef.current = false
    const held = Date.now() - pressedAt.current
    if (held < LOCK_MS) {
      onLock()
      return
    }
    buzz(14)
    onCommit()
  }, [onCommit, onLock])

  // A pointer that leaves the window never fires pointerup on the button.
  // Without this the mic stays open and the parent never finds out.
  useEffect(() => {
    if (!recording || locked) return
    const onUp = () => release()
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [recording, locked, release])

  const label = disabled
    ? 'Write a note'
    : locked
      ? 'Tap to save this note'
      : recording
        ? 'Release to save'
        : 'Hold to record a note'

  return (
    <button
      ref={ref}
      type="button"
      className={`capture-btn${recording ? ' is-recording' : ''}${locked ? ' is-locked' : ''}${disabled ? ' is-textonly' : ''}`}
      aria-label={label}
      aria-pressed={recording}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return
        e.preventDefault()
        press()
      }}
      onPointerUp={(e) => {
        e.preventDefault()
        release()
      }}
      // Space and Enter hold the same way, so this works from a keyboard.
      onKeyDown={(e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return
        e.preventDefault()
        if (e.repeat || heldRef.current || locked) return
        press()
      }}
      onKeyUp={(e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return
        e.preventDefault()
        if (locked) { onCommit(); return }
        release()
      }}
    >
      <span className="capture-btn-ring" aria-hidden="true" />
      <span className="capture-btn-glyph">{locked ? <StopIcon /> : <MicIcon />}</span>
    </button>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}

const MicIcon = () => (
  <svg {...S} width="24" height="24">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4" />
  </svg>
)

const StopIcon = () => (
  <svg {...S} width="20" height="20">
    <rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor" stroke="none" />
  </svg>
)
